import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { toast } from 'react-hot-toast';
import { getLockedMessage } from '@/utils/challengeAccess';
import type { Game, GameChallenge } from '@/types/game';

interface Challenge {
  id: number;
  gameId: string;
  publishDate: string;
  puzzleNumber: number;
  puzzleName: string;
  status: 'today' | 'completed' | 'locked' | 'future' | 'available';
}

interface UseChallengeClickParams {
  game: Game;
  onShowUpsellModal?: (reason: string) => void;
}

interface UseChallengeClickReturn {
  handleChallengeClick: (challenge: Challenge) => void;
  isNavigating: boolean;
  error: string | null;
}

/**
 * Centralized hook for handling challenge click events across the application
 *
 * Handles:
 * - Navigation to playable challenges
 * - Showing upsell modal for locked challenges
 * - Authentication checks
 * - Loading states during navigation
 * - Error handling
 *
 * @param game - The game configuration
 * @param onShowUpsellModal - Callback to show premium upsell modal
 * @returns Object with handleChallengeClick function, loading state, and error state
 *
 * @example
 * ```tsx
 * const { handleChallengeClick, isNavigating } = useChallengeClick({
 *   game,
 *   onShowUpsellModal: () => setModalOpen(true)
 * });
 *
 * <button
 *   onClick={() => handleChallengeClick(challenge)}
 *   disabled={isNavigating}
 * >
 *   Play Challenge
 * </button>
 * ```
 */
export function useChallengeClick({
  game,
  onShowUpsellModal,
}: UseChallengeClickParams): UseChallengeClickReturn {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const [isNavigating, setIsNavigating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const handleChallengeClick = useCallback((challenge: Challenge) => {
    // Reset error state
    setError(null);

    // Clear any existing timeout from previous clicks
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Rule 0: Future challenges are not clickable (should be disabled in UI)
    if (challenge.status === 'future') {
      return;
    }

    // Rule 1: Locked challenges show upsell modal with reason
    if (challenge.status === 'locked') {
      // Generate user-friendly reason message
      const reason = getLockedMessage(challenge as GameChallenge, game);

      if (onShowUpsellModal) {
        onShowUpsellModal(reason);
      } else {
        // Fallback: show error toast if no modal handler provided
        const errorMsg = 'Premium subscription required to access past challenges';
        setError(errorMsg);
        toast.error(errorMsg);
      }
      return;
    }

    // Rule 2: Accessible challenges (today/completed) navigate to game
    // Auth check: If not signed in, store intent and redirect to auth
    if (!isSignedIn) {
      // Store navigation intent in session storage
      sessionStorage.setItem('intendedGame', game.id);
      sessionStorage.setItem('intendedChallenge', challenge.id.toString());

      // Navigate to games page which will trigger auth flow
      navigate('/games');
      return;
    }

    // Navigate to game with challenge ID
    try {
      setIsNavigating(true);

      // Set timeout to prevent infinite loading (10 seconds as per PR plan)
      timeoutRef.current = setTimeout(() => {
        setIsNavigating(false);
        const timeoutMsg = 'Navigation took too long. Please try again.';
        setError(timeoutMsg);

        // Show error toast with retry button
        toast.error(timeoutMsg, {
          duration: 6000,
          id: 'navigation-timeout',
        });
        timeoutRef.current = null;
      }, 10000);

      // Navigation URL: /games/:gameId/play/:challengeId
      const gameUrl = `/games/${game.id}/play/${challenge.id}`;

      // Pass game data in navigation state so GameFrame can load the game
      navigate(gameUrl, { state: { game } });

      // Note: Timeout will be cleared by useEffect cleanup on unmount (successful navigation)
      // or by the timeout callback itself if it fires (failed/slow navigation)
    } catch (err) {
      // Clear timeout on error
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      setIsNavigating(false);
      const errorMsg = err instanceof Error ? err.message : 'Failed to navigate to challenge';
      setError(errorMsg);

      // Show error toast (simple string version to avoid JSX parsing issues)
      toast.error(`${errorMsg} - Please try again.`, {
        duration: 6000,
        id: 'navigation-error',
      });
    }
  }, [game, navigate, isSignedIn, onShowUpsellModal]);

  return {
    handleChallengeClick,
    isNavigating,
    error,
  };
}
